/* eslint-disable no-param-reassign */
import React, { useMemo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { omit, find } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AddImageWrapper } from '@bingads-webui-campaign-react/asset-library-creative-components';
import { getAssetSourceOptions, CSMLAssetSource } from '@bingads-webui-campaign-react/asset-library-model';
import { AddImageContainer as AddLogoContainer } from '@bingads-webui-campaign-react/image-experience';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { getImageValidationErrorMessage } from '@bingads-webui-campaign/image-experience-utils';

import { getAddImageConfig, VIEWS } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import { useWebsiteSuggestion } from '@bingads-webui-campaign-react/add-image-hooks';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { instrumentationScenarios } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';

import { EditVideoAdsView } from './view';
import { getGlobalLogoPickerStyles } from './styles';

export const VideoEditorWithAddImageWrapperImp = ({
  assetLibraryConfig,
  dataService,
  copilotDataService,
  i18n,
  initialView,
  isAssetLibraryAIGCMVPEnabled,
  isLiteLandingPageEnabled,
  logger,
  assetLibraryUserPreferences,
  assetLibraryAccountPreferences,
  ianaTimeZone,
  onApply,
  isOpen,
  setIsOpen,
  videoMetaData,
  cid,
  aid,
  oDataConfig,
  finalUrl,
  permissions,
  isCTV,
  ...rest
}) => {
  const [showAddImagePanel, setShowAddImagePanel] = useState(false);
  const [newImageDataObject, setNewImageDataObject] = useState({});
  const [minSizeOfImage, setMinSizeOfImage] = useState([0, 0]);
  const [aspectRatioOption, setAspectRatioOption] = useState(constants.defaultAspectRatioOption);
  const [showAddLogoPanel, setShowAddLogoPanel] = useState(false);
  const [newLogoDataObject, setNewLogoDataObject] = useState({});

  assetLibraryConfig.assetLibraryUserPreferences = assetLibraryUserPreferences;
  assetLibraryConfig.assetLibraryAccountPreferences = assetLibraryAccountPreferences;
  assetLibraryConfig.logger = logger;
  assetLibraryConfig.ianaTimeZone = ianaTimeZone;
  assetLibraryConfig.onImageAddError = (e) => {
    const errorCode = e && e.errors && e.errors[0] && e.errors[0].Code;
    const errorMessage = getImageValidationErrorMessage({ message: errorCode }, i18n);
    return errorMessage;
  };

  const addMediaConfig = useMemo(() => {
    let config = getAddImageConfig({ isAssetLibraryAIGCMVPEnabled, isLiteLandingPageEnabled, hideStockImage: true });
    if (!permissions.IsSocialMediaImagesEnabled) {
      config = omit(config, VIEWS.SocialMedia);
    }
    return config;
  }, [isAssetLibraryAIGCMVPEnabled, isLiteLandingPageEnabled, permissions.IsSocialMediaImagesEnabled]);

  const mediaRepository = useMemo(() => new MediaRepository({
    cid, aid, oDataConfig,
  }), [aid, cid, oDataConfig]);

  const onSelectLogo = useCallback((images) => {
    const selectedImage = find(images, image => image.isIncluded);
    setNewLogoDataObject({ url: selectedImage.src, id: selectedImage.id });
    setShowAddLogoPanel(false);
  }, []);

  const instrumentation = useContext(InstrumentationContext);

  const createScenario = instrumentation.copilotVideoEditorScenario.create();
  const activity = createScenario.imageSuggestion.create();

  const adCreatorScenario = instrumentationScenarios.CopilotVideoAd;

  const {
    suggestionState,
    suggestionPoller,
  } = useWebsiteSuggestion({
    oDataConfig,
    adContext: { // Use the ad context to compose the API request url
      advertiserCustomerId: cid,
      accountId: aid,
    },
    instrumentation,
    logActivity: activity.trace,
    permissions,
    fullUrl: finalUrl,
    adCreatorScenario,
  });

  const { suggestion = {} } = suggestionState;

  const preDefinedAssetSources = useMemo(() => {
    const assetOptions = getAssetSourceOptions(i18n);
    const assetOptionsWithoutShutterstock = assetOptions.filter(option => option.key !== CSMLAssetSource.Shutterstock);
    const assetOptionsKeys = assetOptionsWithoutShutterstock.map(option => option.key);
    return assetOptionsKeys;
  }, [i18n]);

  const preDefinedFilter = useMemo(() => `AssetSourceType in (${preDefinedAssetSources.map(s => `'${s}'`).join(',')})`, [preDefinedAssetSources]);

  return (
    <div className="video-editor-with-add-image-wrapper">
      <EditVideoAdsView
        onApply={onApply}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        videoMetaData={videoMetaData}
        cid={cid}
        aid={aid}
        oDataConfig={oDataConfig}
        finalUrl={finalUrl}
        dataService={dataService}
        copilotDataService={copilotDataService}
        logger={logger}
        onChangeImage={(minSize) => {
          setShowAddImagePanel(true);
          setMinSizeOfImage(minSize);
        }}
        newImageDataObject={newImageDataObject}
        assetLibraryConfig={assetLibraryConfig}
        onChangeLogo={() => setShowAddLogoPanel(true)}
        newLogoDataObject={newLogoDataObject}
        isCTV={isCTV}
      />
      <AddImageWrapper
        addMediaConfig={addMediaConfig}
        assetLibraryConfig={assetLibraryConfig}
        dataService={dataService}
        i18n={i18n}
        initialView={VIEWS.LandingPage}
        isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
        logger={logger}
        setNewImageDataObject={setNewImageDataObject}
        showAddImagePanel={showAddImagePanel}
        setShowAddImagePanel={setShowAddImagePanel}
        imageExperienceProps={{
          suggestion,
          suggestionPoller,
          finalUrl,
          finalUrlProtocol: 'https://',
          onUpdateFinalUrl: () => {},
          onUpdateFinalUrlPrefix: () => {},
          isMultimediaAd: false,
          isDisplayAds: true,
        }}
        cid={cid}
        aid={aid}
        minSize={minSizeOfImage}
        disabledAssetSources={[CSMLAssetSource.Shutterstock]}
        {...rest}
      />
      <AddLogoContainer
        isImageSelectorDisplayed={showAddLogoPanel}
        onSelectedImagesSet={onSelectLogo}
        onImageSelectionCanceled={() => setShowAddLogoPanel(false)}
        imageType={null}
        i18n={i18n}
        perfMarker={rest.perfMarker}
        isForVNext
        isMultimediaAd
        isAudienceAd={false}
        cid={cid}
        aid={aid}
        isStockPhotoEnabled
        oDataConfig={oDataConfig}
        isLazyLoadEnabled
        permissions={permissions}
        isMultimediaAdinMSANEnabled={false}
        staticImagePath=""
        imageList={[]}
        maxUploadNum={1}
        imageOption={constants.imageOptionKeys.Logo}
        imageOptions={[constants.imageOptionKeys.Logo]}
        setImageOption={() => {}}
        aspectRatioOption={aspectRatioOption}
        setAspectRatioOption={setAspectRatioOption}
        suggestion={suggestion}
        suggestionPoller={suggestionPoller}
        isAdCreatorEnabled
        isFinalUrlImageRetrievalEnabled
        isLogoEnabled
        mediaRepository={mediaRepository}
        onUpdateFinalUrlPrefix={() => {}}
        onUpdateFinalUrl={() => {}}
        finalUrl={finalUrl}
        finalUrlProtocol="https://"
        defaultSelectedPill={0}
        adCreatorScenario={adCreatorScenario}
        isThumbsUpDownEnabled={false}
        isTestEnv={false}
        activityLogger={activity}
        recommendationStockImages={[]}
        hideStockPillControl
        preDefinedFilter={preDefinedFilter}
      />
    </div>
  );
};

VideoEditorWithAddImageWrapperImp.propTypes = {
  assetLibraryConfig: PropTypes.shape({
    activityLogger: PropTypes.shape({}),
  }).isRequired,
  dataService: PropTypes.shape({}).isRequired,
  copilotDataService: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  initialView: PropTypes.string.isRequired,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool.isRequired,
  isLiteLandingPageEnabled: PropTypes.bool.isRequired,
  logger: PropTypes.shape({}).isRequired,
  assetLibraryUserPreferences: PropTypes.shape({}).isRequired,
  assetLibraryAccountPreferences: PropTypes.shape({}).isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  videoMetaData: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.string),
    text: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  finalUrl: PropTypes.string.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  isCTV: PropTypes.bool,
};

VideoEditorWithAddImageWrapperImp.defaultProps = {
  isCTV: false,
};

export const VideoEditorWithAddImageWrapper = withDefaultStyles(VideoEditorWithAddImageWrapperImp, getGlobalLogoPickerStyles);
